import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { forgotPasswordSendLink, forgotPasswordReset } from '../../services/api';
import logo from "../../assets/img/logo.png";
import { toast } from "react-toastify";

export const ForgotPassword: React.FC = () => {
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState<'email' | 'reset-password' | 'success'>('email');
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [resetToken, setResetToken] = useState<string>('');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const token = searchParams.get('token');
        if (token) {
            setResetToken(token);
            setStep('reset-password');
        }
    }, [searchParams]);

    const validateEmailForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!email) {
            newErrors.email = 'Please enter your email address.';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter a valid email address.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateEmailField = (value: string) => {
        const newErrors = { ...errors };

        if (!value) {
            newErrors.email = 'Please enter your email address.';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
            newErrors.email = 'Please enter a valid email address.';
        } else {
            delete newErrors.email;
        }

        setErrors(newErrors);
    };

    const validatePasswordForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!newPassword) {
            newErrors.newPassword = 'New password is required';
        } else if (newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters long';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(newPassword)) {
            newErrors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'newPassword') {
            if (!value) {
                newErrors.newPassword = 'New password is required';
            } else if (value.length < 8) {
                newErrors.newPassword = 'Password must be at least 8 characters long';
            } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(value)) {
                newErrors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)';
            } else {
                delete newErrors.newPassword;
            }

            // Also validate confirm password when new password changes
            if (confirmPassword && confirmPassword !== value) {
                newErrors.confirmPassword = 'Passwords do not match';
            } else if (confirmPassword && confirmPassword === value) {
                delete newErrors.confirmPassword;
            }
        } else if (fieldName === 'confirmPassword') {
            if (!value) {
                newErrors.confirmPassword = 'Please confirm your password';
            } else if (newPassword !== value) {
                newErrors.confirmPassword = 'Passwords do not match';
            } else {
                delete newErrors.confirmPassword;
            }
        }

        setErrors(newErrors);
    };

    const handleSendResetCode = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateEmailForm()) {
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            await forgotPasswordSendLink(email);
            toast.success("Password reset link sent successfully! Check your email.");
            setStep('success');
        } catch (error: any) {
            // Handle different types of errors
            let errorMessage = 'Failed to send reset link. Please try again.';

            if (error.response?.status === 404) {
                // Email not found
                errorMessage = "This email is not registered with us. Please check your email address or contact support.";
            } else if (error.response?.status === 500) {
                // Server error - do not expose raw backend detail
                errorMessage = "Failed to send reset link. Please try again.";
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validatePasswordForm()) {
            return;
        }

        if (!resetToken) {
            toast.error("Reset token is missing. Please request a new reset link.");
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            await forgotPasswordReset(resetToken, newPassword, confirmPassword);
            toast.success("Password reset successfully! You can now login with your new password.");
            setStep('success');
        } catch (error: any) {
            const errorMessage = error.message || 'Failed to reset password. Please try again.';
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    const handleCancel = () => {
        navigate('/login');
    };

    const handleBackToEmail = () => {
        setStep('email');
        setErrors({});
    };

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    // Success state
    if (step === 'success') {
        return (
            <>
                <div className='auth-wrapper'>
                    <div className='auth-box'>
                        <div className='auth-header text-center'>
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h1 className='mb-2'>Success!</h1>
                            <p className='text-16-reg grey-500 mb-5'>
                                {resetToken ?
                                    'Your password has been reset successfully! You can now login with your new password.' :
                                    'We\'ve sent a password reset link to your email. Click the link in your email to set a new password.'
                                }
                            </p>
                            <button className='renegade-btn-primary w-100' onClick={handleBackToLogin}>
                                <i className="iconoir-arrow-left"></i>
                                Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Password reset step
    if (step === 'reset-password') {
        return (
            <>
                <div className='auth-wrapper'>
                    <div className='auth-box reset-password-box'>
                        <div className='auth-header text-center'>
                            <img className='auth-logo mb-2' src={logo} alt="Renegade Logo" />
                            <h1 className='mb-2'>Reset Your Password</h1>
                            <p className='text-16-reg grey-500 mb-4'>Set your new password below.</p>
                            <svg className='mx-auto mb-4' width="129" height="5" viewBox="0 0 129 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.5" width="81" height="5" rx="2.5" fill="#DA291C" />
                                <rect x="88.5" width="22" height="5" rx="2.5" fill="#BDBDBD" />
                            </svg>
                        </div>
                        <div className="auth-body">
                            <form onSubmit={handleResetPassword} className="space-y-6">
                                <div className="form-group password-feild">
                                    <input
                                        id="newPassword"
                                        type={showNewPassword ? 'text' : 'password'}
                                        placeholder="Enter new password"
                                        value={newPassword}
                                        onChange={(e) => {
                                            setNewPassword(e.target.value);
                                            // Real-time validation
                                            validatePasswordField('newPassword', e.target.value);
                                        }}
                                        className={`form-control ${errors.newPassword ? 'border-danger' : ''}`}
                                    />
                                    <button type="button" onClick={toggleNewPasswordVisibility} className='password-toggle'>
                                        {showNewPassword ? (
                                            <i className="iconoir-eye"></i>
                                        ) : (
                                            <i className="iconoir-eye-closed"></i>
                                        )}
                                    </button>
                                    {errors.newPassword && (
                                        <p className="text-danger">{errors.newPassword}</p>
                                    )}
                                </div>

                                <div className="form-group password-feild">
                                    <input
                                        id="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Enter confirm password"
                                        value={confirmPassword}
                                        onChange={(e) => {
                                            setConfirmPassword(e.target.value);
                                            // Real-time validation
                                            validatePasswordField('confirmPassword', e.target.value);
                                        }}
                                        className={`form-control ${errors.confirmPassword ? 'border-danger' : ''}`}
                                    />
                                    <button className='password-toggle' type="button" onClick={toggleConfirmPasswordVisibility} >
                                        {showConfirmPassword ? (
                                            <i className="iconoir-eye"></i>
                                        ) : (
                                            <i className="iconoir-eye-closed"></i>
                                        )}
                                    </button>
                                    {errors.confirmPassword && (
                                        <p className="text-danger">{errors.confirmPassword}</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="renegade-btn-primary w-100 mt-3"
                                >
                                    {isLoading ? 'Resetting...' : 'Reset Password'}
                                </button>
                            </form>
                            <div className="mt-4 d-flex justify-content-between">
                                <button
                                    onClick={handleBackToEmail}
                                    className="flex items-center text-gray-700 hover:text-red-500 transition-colors"
                                    style={{
                                        fontFamily: 'Arial',
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        lineHeight: '145%'
                                    }}
                                >
                                    <img src="/icons/backButton.png" alt="Back" className="w-3 h-2 mr-2" />
                                    Back
                                </button>
                                <button
                                    onClick={handleCancel}
                                    className="flex items-center text-gray-700 hover:text-red-500 transition-colors"
                                    style={{
                                        fontFamily: 'Arial',
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        lineHeight: '145%'
                                    }}
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Back to Login
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Email step (initial step)
    return (
        <>
            <div className='auth-wrapper'>
                <div className='auth-box'>
                    <div className='auth-header text-center'>
                        <img className='auth-logo mb-2' src={logo} alt="Renegade Logo" />
                        <h1 className='mb-2'>Forgot Your Password</h1>
                        <p className='text-16-reg grey-500 mb-4'>Enter your email and we’ll send you link to reset your password.</p>
                        <svg className='mx-auto mb-4' width="129" height="5" viewBox="0 0 129 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.5" width="81" height="5" rx="2.5" fill="#DA291C" />
                            <rect x="88.5" width="22" height="5" rx="2.5" fill="#BDBDBD" />
                        </svg>
                    </div>
                    <div className="auth-body">
                        <form onSubmit={handleSendResetCode}>
                            <div className="form-group">
                                <input
                                    id="email"
                                    type="text"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        // Real-time validation
                                        validateEmailField(e.target.value);
                                    }}
                                    className={`form-control ${errors.email ? 'border-danger' : ''}`}
                                />
                                {errors.email && (
                                    <p className="error-message">{errors.email}</p>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="renegade-btn-primary w-100 mt-3"
                                style={{
                                    fontFamily: 'Arial',
                                }}
                            >
                                {isLoading ? 'Sending...' : 'Send Reset Password Link'}
                            </button>
                        </form>
                        <Link className='text-16-bold grey-900 d-flex justify-content-center align-items-center mt-4' to="/login">
                            <i className="iconoir-arrow-left mr-2"></i>
                            Back to Login
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
};
